import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Spin, Tag } from 'antd';
import { useApiToken } from '../hooks/useApiToken';
import { apiRequest } from '../utils/integrationsApi';
import { impersonationHeaders, getActiveImpersonation } from '../utils/impersonationFetch';
import { formatDdMmYyyy } from '../utils/formatDdMmYyyy';
import { userMessageForError } from '../utils/apiError';

/**
 * Risk close / reopen + state-change history on the approved risk register.
 * ISO 27001 Cl. 6.1.3(f) residual-risk acceptance, Cl. 8.3, Cl. 9.3.2(c); Workflow Stage 6 (Risk analysis).
 * Backend: riskHistory.controller.ts —
 *   POST /projects/:id/risk-register/risks/:rowUid/close | /reopen   { reason }   (lead_advisor only)
 *   GET  /projects/:id/risk-register/history                          (any project member)
 *
 * Gating here only mirrors the API (Commandment III/V): the server is the authority and every refusal
 * (400/403/404/409) is shown with its own message. Open/closed state comes ONLY from the history
 * endpoint; the approved register row is never edited (Commandment VII). Nothing is applied
 * optimistically — the badge changes only after the server accepts and the history is re-read.
 * No backfill exists: history starts when tracking began, and the UI says so rather than imply more.
 */

export const REASON_MAX = 1000;

const EVENT_LABEL = {
    RAISED: 'Raised',
    RATING_CHANGED: 'Rating changed',
    TREATMENT_PUBLISHED: 'Treatment decision published',
    WITHDRAWN: 'Withdrawn during authoring',
    CLOSED: 'Closed',
    REOPENED: 'Reopened',
};
const ROLE_LABEL = {
    lead_advisor: 'Lead Advisor',
    advisor: 'Advisor',
    company_spoc: 'Company SPOC',
    company_delegate: 'Company Delegate',
    platform_admin: 'ProLense Admin',
};

export function describeLifecycleError(err, kind) {
    const verb = kind === 'CLOSED' ? 'close' : 'reopen';
    const serverMsg = err?.message;
    if (err?.status === 403) return serverMsg || `Only the Lead Advisor can ${verb} a risk.`;
    if (err?.status === 409) return serverMsg || `This risk cannot be ${verb === 'close' ? 'closed' : 'reopened'} in its current state.`;
    if (err?.status === 400) return serverMsg || 'A reason is required.';
    if (err?.status === 404) return serverMsg || 'That risk was not found in the register.';
    return userMessageForError(err, `Could not ${verb} the risk. Nothing was changed.`);
}

/** Loads the project's risk history once; exposes per-risk lookups. */
export function useRiskHistory(projectId, enabled = true) {
    const getToken = useApiToken();
    const [state, setState] = useState({ phase: 'loading', transitions: [], closed: [], error: null });

    const load = useCallback(async () => {
        if (!projectId) return;
        setState((s) => ({ ...s, phase: 'loading', error: null }));
        try {
            const data = await apiRequest(
                getToken,
                `/projects/${encodeURIComponent(projectId)}/risk-register/history`,
                { headers: impersonationHeaders() },
            );
            setState({
                phase: 'ready',
                transitions: data?.transitions ?? [],
                closed: data?.closedRiskRowUids ?? [],
                error: null,
            });
        } catch (err) {
            setState({ phase: 'error', transitions: [], closed: [], error: userMessageForError(err, 'Could not load risk history.') });
        }
    }, [getToken, projectId]);

    useEffect(() => {
        if (enabled) load();
    }, [enabled, load]);

    const closedSet = useMemo(() => new Set(state.closed), [state.closed]);
    return useMemo(() => ({ ...state, closedSet, reload: load }), [state, closedSet, load]);
}

export function RiskOpenClosedBadge({ history, rowUid }) {
    if (history.phase !== 'ready') return null; // never guess a status the server has not given
    return history.closedSet.has(rowUid) ? (
        <Tag color="default">Closed</Tag>
    ) : (
        <Tag color="blue">Open</Tag>
    );
}

function TransitionDetail({ t }) {
    const parts = [];
    if (t.eventType === 'RATING_CHANGED' || t.eventType === 'RAISED') {
        if (t.fromRiskScore != null || t.toRiskScore != null) {
            parts.push(`Score: ${t.fromRiskScore ?? '-'} → ${t.toRiskScore ?? '-'}`);
        }
        if (t.fromSignificance || t.toSignificance) {
            parts.push(`Significance: ${t.fromSignificance ?? '-'} → ${t.toSignificance ?? '-'}`);
        }
    }
    if (t.eventType === 'CLOSED') parts.push('Status: Open → Closed');
    if (t.eventType === 'REOPENED') parts.push('Status: Closed → Open');
    return (
        <>
            {parts.map((p) => (
                <div key={p} className="text-xs text-[color:var(--color-text-muted)]">{p}</div>
            ))}
            {t.reason ? <div className="text-xs text-[color:var(--color-text-muted-dark)]">Reason: {t.reason}</div> : null}
        </>
    );
}

export function RiskHistoryTimeline({ history, rowUid }) {
    if (history.phase === 'loading') return <Spin />;
    if (history.phase === 'error') {
        return <Alert type="error" showIcon message="Could not load risk history" description={history.error} />;
    }
    const items = history.transitions.filter((t) => t.riskRowUid === rowUid);
    if (items.length === 0) {
        return (
            <div data-testid="empty" className="text-sm text-[color:var(--color-text-muted)]">
                No recorded changes yet - history starts from when tracking began.
            </div>
        );
    }
    return (
        <ol className="m-0 list-none space-y-3 p-0" data-testid="risk-history-list">
            {items.map((t) => (
                <li key={t.id} className="border-l-2 border-[color:var(--color-border-strong)] pl-3">
                    <div className="text-sm font-semibold">{EVENT_LABEL[t.eventType] || t.eventType}</div>
                    <div className="text-xs text-[color:var(--color-text-muted)]">
                        {formatDdMmYyyy(t.occurredAt)} · {t.actorEmail || 'System'}
                        {t.actorRole ? ` (${ROLE_LABEL[t.actorRole] || t.actorRole})` : ''}
                    </div>
                    <TransitionDetail t={t} />
                </li>
            ))}
        </ol>
    );
}

/**
 * Row actions. `isLeadAdvisor` is the strict Lead Advisor role (NOT platform_admin — the API refuses it).
 * `registerApproved` mirrors workflow === 'finalized'.
 */
export function RiskRowLifecycleActions({ projectId, record, history, isLeadAdvisor, registerApproved }) {
    const getToken = useApiToken();
    const [dialog, setDialog] = useState(null); // 'CLOSED' | 'REOPENED' | null
    const [showHistory, setShowHistory] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [reasonTouched, setReasonTouched] = useState(false);

    const impersonating = !!getActiveImpersonation(); // effective role may not be LA; API would refuse
    const canAct = isLeadAdvisor && !impersonating;
    // Open/closed is the server's answer from the history endpoint; until it has arrived (or if it
    // failed) we do not guess — the action is disabled instead.
    const historyReady = history.phase === 'ready';
    const isClosed = historyReady && history.closedSet.has(record.rowUid);
    const kind = isClosed ? 'REOPENED' : 'CLOSED';
    const trimmed = reason.trim();

    const closeDialog = () => {
        if (busy) return;
        setDialog(null);
        setReason('');
        setError(null);
        setReasonTouched(false);
    };

    const submit = async () => {
        setReasonTouched(true);
        if (!trimmed) return;
        setBusy(true);
        setError(null);
        try {
            await apiRequest(
                getToken,
                `/projects/${encodeURIComponent(projectId)}/risk-register/risks/${encodeURIComponent(record.rowUid)}/${kind === 'CLOSED' ? 'close' : 'reopen'}`,
                { method: 'POST', body: { reason: trimmed }, headers: impersonationHeaders() },
            );
            setBusy(false);
            setDialog(null);
            setReason('');
            setReasonTouched(false);
            await history.reload();
        } catch (err) {
            setBusy(false);
            setError(describeLifecycleError(err, kind));
            if (err?.status === 403 || err?.status === 409) history.reload();
        }
    };

    return (
        <Space size="small" wrap>
            <Button type="link" size="small" onClick={() => setShowHistory(true)}>
                History
            </Button>
            {canAct && registerApproved ? (
                <Button
                    type="link"
                    size="small"
                    danger={!isClosed}
                    disabled={!historyReady}
                    title={historyReady ? undefined : history.phase === 'error'
                        ? 'Risk history could not be loaded, so the current status is unknown.'
                        : 'Waiting for the current risk status to load.'}
                    onClick={() => setDialog(kind)}
                >
                    {isClosed ? 'Reopen risk' : 'Close risk'}
                </Button>
            ) : null}

            <Modal
                title={`History - ${record.riskId || record.rowUid}`}
                open={showHistory}
                onCancel={() => setShowHistory(false)}
                footer={null}
                destroyOnClose
                width={560}
            >
                <RiskHistoryTimeline history={history} rowUid={record.rowUid} />
            </Modal>

            <Modal
                title={kind === 'CLOSED' ? 'Close this risk?' : 'Reopen this risk?'}
                open={!!dialog}
                onCancel={closeDialog}
                destroyOnClose
                footer={
                    <Space>
                        <Button onClick={closeDialog} disabled={busy}>Cancel</Button>
                        <Button type="primary" danger={kind === 'CLOSED'} loading={busy} onClick={submit}>
                            {kind === 'CLOSED' ? 'Close risk' : 'Reopen risk'}
                        </Button>
                    </Space>
                }
            >
                <p className="m-0 mb-2 text-sm">
                    {record.riskId ? `${record.riskId}: ` : ''}
                    {record.riskTitle || record.riskStatement || ''}
                </p>
                <Alert
                    type="warning"
                    showIcon
                    className="mb-3"
                    message="This action is recorded permanently in the audit history, together with your name, the date and your reason. It cannot be edited or deleted."
                />
                <label className="mb-1 block text-sm font-semibold" htmlFor={`risk-reason-${record.rowUid}`}>
                    Reason (required)
                </label>
                <Input.TextArea
                    id={`risk-reason-${record.rowUid}`}
                    rows={3}
                    maxLength={REASON_MAX}
                    showCount
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                        kind === 'CLOSED'
                            ? 'e.g. Treatment completed and residual risk accepted by the risk owner'
                            : 'e.g. New incident shows the control is not effective'
                    }
                />
                {reasonTouched && !trimmed ? (
                    <div role="alert" className="mt-1 text-xs text-[color:var(--color-danger-fg)]">A reason is required.</div>
                ) : null}
                {error ? <Alert className="mt-3" type="error" showIcon message={error} /> : null}
            </Modal>
        </Space>
    );
}
